import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, AlertTriangle, Info, Pin, PinOff, Umbrella } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { cn } from '../../lib/utils';
import type {
  PlanAbsence,
  PlanAssignmentInfo,
  PlanBoardEmployee,
  PlanConstraintInfo,
  PlanSlotInfo,
  PlanViolation,
} from './types';
import {
  DAY_OFF_CLASSES,
  dayCodeOf,
  employeeName,
  longDate,
  slotColorClass,
  slotHours,
  sortSlots,
} from './planningUtils';
import { ExceptionMark } from './PlanBoard';

export const NOTE_MAX_LENGTH = 300;

const OFF = '__OFF__';

export interface PlanCellEditorTarget {
  employee: PlanBoardEmployee;
  date: string;
}

interface PlanCellEditorProps {
  target: PlanCellEditorTarget | null;
  assignment?: PlanAssignmentInfo;
  absence?: PlanAbsence;
  holidayName?: string;
  violations: PlanViolation[];
  /** Confirmed exceptions covering this cell. */
  exceptions?: PlanConstraintInfo[];
  slotById: Map<string, PlanSlotInfo>;
  /** null = user may edit; otherwise the reason why the cell is read-only. */
  readOnlyReason: string | null;
  busy: boolean;
  onSave: (shiftTimeId: string | null, note: string) => void;
  onClear: () => void;
  onTogglePin: (isPinned: boolean) => void;
  onClose: () => void;
}

export const PlanCellEditor: React.FC<PlanCellEditorProps> = ({
  target,
  assignment,
  absence,
  holidayName,
  violations,
  exceptions,
  slotById,
  readOnlyReason,
  busy,
  onSave,
  onClear,
  onTogglePin,
  onClose,
}) => {
  const initialSelection = assignment
    ? assignment.slotStart
      ? assignment.shiftTimeId ?? null
      : OFF
    : null;
  const initialNote = assignment?.note ?? '';

  const [selection, setSelection] = useState<string | null>(initialSelection);
  const [note, setNote] = useState(initialNote);

  // Reset local state whenever another cell is opened or the stored value changes.
  const targetKey = target ? `${target.employee.employeeId}|${target.date}` : '';
  useEffect(() => {
    setSelection(initialSelection);
    setNote(initialNote);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey, assignment?.id, assignment?.shiftTimeId, assignment?.slotStart, assignment?.note]);

  const allowedSlots = useMemo(() => {
    if (!target) return [];
    return sortSlots(
      target.employee.allowedSlotIds
        .map((id) => slotById.get(id))
        .filter((s): s is PlanSlotInfo => !!s),
    );
  }, [target, slotById]);

  if (!target) return null;

  const { employee, date } = target;
  const hasShift = employee.allowedSlotIds.length > 0;
  const editable = readOnlyReason === null && hasShift;
  const dayCode = dayCodeOf(date);

  const noteTooLong = note.length > NOTE_MAX_LENGTH;
  const noteError = noteTooLong
    ? `Notatka może mieć najwyżej ${NOTE_MAX_LENGTH} znaków (teraz: ${note.length}).`
    : '';
  const unchanged = selection === initialSelection && note.trim() === initialNote.trim();
  const canSave = editable && !busy && selection !== null && !noteTooLong && !unchanged;

  const removedSlot = assignment && assignment.slotStart && !assignment.shiftTimeId;
  const hardViolations = violations.filter((v) => v.severity === 'HARD');
  const softViolations = violations.filter((v) => v.severity !== 'HARD');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    onSave(selection === OFF ? null : selection, note.trim());
  };

  return (
    <Dialog open={!!target} onOpenChange={(next) => !next && !busy && onClose()}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto" data-testid="plan-cell-editor">
        <DialogHeader>
          <DialogTitle>{employeeName(employee)}</DialogTitle>
          <DialogDescription>
            <span>{longDate(date)}</span>
            {employee.positionName ? ` · ${employee.positionName}` : ''}
          </DialogDescription>
        </DialogHeader>

        <form id="plan-cell-form" onSubmit={handleSubmit} className="space-y-4">
          {holidayName && (
            <p className="text-sm rounded-md border bg-muted/60 px-3 py-2">Święto: {holidayName}</p>
          )}

          {exceptions && exceptions.length > 0 && (
            <ul
              className="space-y-1 rounded-md border border-primary/40 bg-primary/5 px-3 py-2 text-sm"
              data-testid="plan-cell-exceptions"
            >
              {exceptions.map((c) => (
                <li key={c.id} className="flex items-start gap-2">
                  <ExceptionMark className="mt-0.5 shrink-0" />
                  <span>Wyjątek: {c.description}</span>
                </li>
              ))}
            </ul>
          )}

          {absence && (
            <div
              className={cn(
                'flex items-start gap-2 text-sm rounded-md border px-3 py-2',
                absence.status === 'APPROVED'
                  ? 'border-stone-300 bg-stone-100 text-stone-900 dark:border-stone-700 dark:bg-stone-800/60 dark:text-stone-100'
                  : 'border-dashed border-stone-400 text-stone-800 dark:border-stone-500 dark:text-stone-200',
              )}
            >
              <Umbrella className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                {absence.status === 'APPROVED'
                  ? `Zatwierdzona nieobecność: ${absence.label}. Przydzielenie zmiany w tym dniu będzie błędem.`
                  : `Wniosek o nieobecność czeka na decyzję: ${absence.label}.`}
              </span>
            </div>
          )}

          {(hardViolations.length > 0 || softViolations.length > 0) && (
            <ul className="space-y-1 text-sm">
              {hardViolations.map((v, i) => (
                <li key={`h${i}`} className="flex items-start gap-2 text-red-700 dark:text-red-300">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{v.message}</span>
                </li>
              ))}
              {softViolations.map((v, i) => (
                <li key={`s${i}`} className="flex items-start gap-2 text-amber-700 dark:text-amber-300">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{v.message}</span>
                </li>
              ))}
            </ul>
          )}

          {!hasShift && (
            <div className="flex items-start gap-2 text-sm rounded-md border bg-muted/50 px-3 py-2">
              <Info className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
              <span>
                Ten pracownik nie ma przypisanej zmianowości, więc nie można go zaplanować. Przypisz
                zmianowość w danych pracownika (Kadry → Pracownicy) albo ustaw „Domyślną zmianowość”
                dla jego stanowiska (Kadry → Stanowiska).
              </span>
            </div>
          )}

          {readOnlyReason && hasShift && (
            <p className="text-sm text-muted-foreground">{readOnlyReason}</p>
          )}

          {removedSlot && (
            <p className="text-sm text-red-700 dark:text-red-300">
              Zapisana zmiana {slotHours(assignment!.slotStart, assignment!.slotEnd)} nie istnieje już w
              zmianowości pracownika. Wybierz inną zmianę albo wyczyść komórkę.
            </p>
          )}

          {hasShift && (
            <div className="space-y-2">
              <Label>Zmiana</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="group" aria-label="Wybór zmiany">
                {allowedSlots.map((slot) => {
                  const selected = selection === slot.id;
                  const unusualDay = !slot.daysOfWeek.includes(dayCode);
                  return (
                    <button
                      type="button"
                      key={slot.id}
                      data-testid={`slot-option-${slot.id}`}
                      aria-pressed={selected}
                      disabled={!editable || busy}
                      onClick={() => setSelection(slot.id)}
                      className={cn(
                        'flex flex-col items-start rounded-md border px-3 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                        selected
                          ? 'border-primary ring-2 ring-primary bg-primary/5'
                          : 'border-input hover:bg-muted',
                      )}
                    >
                      <span
                        className={cn(
                          'rounded border px-1.5 py-0.5 text-xs font-semibold tabular-nums',
                          slotColorClass(slot.startTime),
                        )}
                      >
                        {slotHours(slot.startTime, slot.endTime)}
                      </span>
                      <span className="mt-1 text-xs text-muted-foreground">{slot.shiftName}</span>
                      {unusualDay && (
                        <span className="text-xs text-amber-700 dark:text-amber-300">
                          Zwykle nie w ten dzień tygodnia
                        </span>
                      )}
                    </button>
                  );
                })}
                <button
                  type="button"
                  data-testid="slot-option-off"
                  aria-pressed={selection === OFF}
                  disabled={!editable || busy}
                  onClick={() => setSelection(OFF)}
                  className={cn(
                    'flex flex-col items-start rounded-md border px-3 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                    selection === OFF
                      ? 'border-primary ring-2 ring-primary bg-primary/5'
                      : 'border-input hover:bg-muted',
                  )}
                >
                  <span className={cn('rounded border px-1.5 py-0.5 text-xs font-semibold', DAY_OFF_CLASSES)}>
                    W
                  </span>
                  <span className="mt-1 text-xs text-muted-foreground">Wolne (dzień wolny)</span>
                </button>
              </div>
              {editable && selection === null && (
                <p className="text-xs text-muted-foreground">Wybierz zmianę albo „Wolne”.</p>
              )}
            </div>
          )}

          {hasShift && (
            <div className="space-y-1">
              <Label htmlFor="plan-cell-note">Notatka (opcjonalnie)</Label>
              <Textarea
                id="plan-cell-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                disabled={!editable || busy}
                placeholder="np. zamiana z inną osobą"
                aria-invalid={noteTooLong}
                className={cn(noteTooLong && 'border-red-500 focus-visible:ring-red-500')}
              />
              <div className="flex justify-between text-xs">
                <span className="text-red-600 dark:text-red-400">{noteError}</span>
                <span className={cn('text-muted-foreground', noteTooLong && 'text-red-600 dark:text-red-400')}>
                  {note.length}/{NOTE_MAX_LENGTH}
                </span>
              </div>
            </div>
          )}

          {assignment && editable && (
            <div className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
              <div className="text-sm">
                <div className="font-medium flex items-center gap-1.5">
                  {assignment.isPinned ? <Pin className="w-4 h-4 text-primary" /> : <PinOff className="w-4 h-4" />}
                  {assignment.isPinned ? 'Komórka przypięta' : 'Komórka nieprzypięta'}
                </div>
                <div className="text-xs text-muted-foreground">
                  Przypiętej komórki nie zmieni automatyczne układanie planu.
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="plan-cell-pin-toggle"
                disabled={busy}
                onClick={() => onTogglePin(!assignment.isPinned)}
              >
                {assignment.isPinned ? 'Odepnij' : 'Przypnij'}
              </Button>
            </div>
          )}
        </form>

        <DialogFooter className="gap-2 sm:justify-between">
          <div>
            {assignment && editable && (
              <Button
                type="button"
                variant="outline"
                className="text-red-600 hover:text-red-700 dark:text-red-400"
                disabled={busy}
                onClick={onClear}
                data-testid="plan-cell-clear"
              >
                Wyczyść
              </Button>
            )}
          </div>
          <div className="flex flex-col-reverse sm:flex-row gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
              {editable ? 'Anuluj' : 'Zamknij'}
            </Button>
            {editable && (
              <Button type="submit" form="plan-cell-form" disabled={!canSave} data-testid="plan-cell-save">
                {busy ? 'Zapisywanie...' : 'Zapisz'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
